import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  Link as LinkIcon,
  Unlink,
  RefreshCw,
  Download,
  Clock,
  Trash2,
  Play,
  Check,
  AlertCircle,
  ExternalLink,
  ChevronRight,
  Settings,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { googleApi } from "@/lib/crmApi";
import { formatDateTime } from "@/lib/crmHelpers";
import GoogleConfigPanel from "@/components/crm/GoogleConfigPanel";

function ConnectPanel({ status, onConnect, onDisconnect, busy, onGoToSettings }) {
  if (!status?.configured) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900/40 dark:bg-amber-950/30">
        <div className="font-heading font-semibold text-amber-900 dark:text-amber-200">
          OAuth Google non configuré
        </div>
        <p className="mt-1 text-amber-800 dark:text-amber-300">
          Ajoutez votre <strong>Client ID</strong> et <strong>Client Secret</strong> dans l'onglet{" "}
          <strong>Paramètres</strong> pour activer la connexion.
        </p>
        <Button
          size="sm"
          onClick={onGoToSettings}
          data-testid="goto-settings-btn"
          className="mt-3 gap-2"
        >
          <Settings className="h-4 w-4" />
          Ouvrir Paramètres
        </Button>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-border/60 bg-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--accent-navy))] text-white">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-semibold">Connecter Google Sheets</div>
            <p className="text-sm text-muted-foreground">
              Autorisez l'accès pour lister et importer vos feuilles
            </p>
          </div>
        </div>
        <Button
          onClick={onConnect}
          disabled={busy}
          data-testid="google-connect-btn"
          className="gap-2"
        >
          <LinkIcon className="h-4 w-4" />
          Se connecter avec Google
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/30">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-4 w-4" strokeWidth={3} />
        </div>
        <div>
          <div className="font-heading text-sm font-semibold">
            Connecté : {status.name || status.email}
          </div>
          <div className="text-xs text-muted-foreground">{status.email}</div>
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onDisconnect}
        disabled={busy}
        data-testid="google-disconnect-btn"
        className="gap-1.5 text-muted-foreground hover:text-red-500"
      >
        <Unlink className="h-3.5 w-3.5" />
        Déconnecter
      </Button>
    </div>
  );
}

function SheetPicker({ onSelect, selected }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { files } = await googleApi.listSheets();
      setSheets(files || []);
    } catch (e) {
      toast.error("Impossible de lister vos Sheets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Vos feuilles Google Sheets</Label>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="google-refresh-sheets"
          className="h-7 gap-1 text-xs"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>
      <div className="scrollbar-thin max-h-[280px] space-y-1 overflow-y-auto rounded-lg border border-border/60 p-1">
        {loading && sheets.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Chargement...
          </div>
        ) : sheets.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Aucune feuille trouvée
          </div>
        ) : (
          sheets.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s)}
              data-testid={`google-sheet-${s.id}`}
              className={`flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-muted ${
                selected?.id === s.id ? "bg-muted ring-1 ring-[hsl(var(--accent-navy))]" : ""
              }`}
            >
              <FileSpreadsheet className="h-4 w-4 flex-none text-emerald-600 dark:text-emerald-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{s.name}</div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {formatDateTime(s.modifiedTime)} ·{" "}
                  {s.owners?.[0]?.displayName || "—"}
                </div>
              </div>
              {s.webViewLink && (
                <a
                  href={s.webViewLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ImportPanel({ sheet, onImported }) {
  const [tabs, setTabs] = useState([]);
  const [tabTitle, setTabTitle] = useState("");
  const [range, setRange] = useState("A:Z");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [autoSync, setAutoSync] = useState(false);
  const [interval, setInterval] = useState(30);

  useEffect(() => {
    if (!sheet) return;
    (async () => {
      try {
        const { tabs } = await googleApi.listTabs(sheet.id);
        setTabs(tabs || []);
        if (tabs?.length) setTabTitle(tabs[0].title);
      } catch (e) {
        toast.error("Impossible de lister les onglets");
      }
    })();
  }, [sheet]);

  const fullRange = tabTitle ? `${tabTitle}!${range}` : range;

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await googleApi.preview(sheet.id, fullRange);
      setPreview(res);
      if (res.total_rows === 0) toast.warning("Aucune ligne de données trouvée");
    } catch (e) {
      toast.error("Aperçu impossible (vérifiez la plage)");
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await googleApi.importSheet(sheet.id, fullRange, skipDup);
      toast.success(
        `Import : ${res.inserted} ajoutés, ${res.skipped} doublons ignorés${
          res.rejected ? `, ${res.rejected} rejetés` : ""
        }`
      );
      if (autoSync) {
        try {
          await googleApi.createSync({
            spreadsheet_id: sheet.id,
            spreadsheet_name: sheet.name,
            range: fullRange,
            interval_minutes: Math.max(5, Number(interval) || 30),
            enabled: true,
          });
          toast.success("Auto-sync activée");
        } catch (e) {
          toast.error("Échec de l'enregistrement de l'auto-sync");
        }
      }
      onImported?.();
    } catch (e) {
      toast.error("Import échoué");
    } finally {
      setImporting(false);
    }
  };

  if (!sheet) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
        Sélectionnez une feuille à gauche pour commencer.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="import-panel">
      <div>
        <div className="text-label text-muted-foreground">Feuille sélectionnée</div>
        <div className="font-heading mt-1 truncate text-base font-semibold">
          {sheet.name}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Onglet</Label>
          <Select value={tabTitle} onValueChange={setTabTitle}>
            <SelectTrigger data-testid="google-tab-select">
              <SelectValue placeholder="Choisir un onglet" />
            </SelectTrigger>
            <SelectContent>
              {tabs.map((t) => (
                <SelectItem key={t.sheet_id} value={t.title}>
                  {t.title} ({t.row_count} lignes)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Plage A1</Label>
          <Input
            value={range}
            onChange={(e) => setRange(e.target.value)}
            placeholder="A:Z ou A1:H1000"
            data-testid="google-range-input"
          />
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handlePreview}
        disabled={previewing || !tabTitle}
        data-testid="google-preview-btn"
        className="gap-2"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${previewing ? "animate-spin" : ""}`} />
        Aperçu & mapping auto
      </Button>

      {preview && (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
          <div className="text-label mb-2 text-muted-foreground">
            Aperçu · {preview.total_rows} ligne(s) détectées
          </div>
          <div className="mb-2 flex flex-wrap gap-1">
            {preview.headers.map((h) => (
              <span
                key={h}
                className="rounded-full bg-background px-2 py-0.5 text-[10px] font-medium"
              >
                {h}
              </span>
            ))}
          </div>
          <div className="scrollbar-thin max-h-[180px] space-y-1.5 overflow-y-auto">
            {preview.preview.slice(0, 6).map((p, i) => (
              <div
                key={i}
                className={`rounded-md border px-2 py-1.5 text-xs ${
                  p.mapped
                    ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                    : "border-red-200 bg-red-50/40 dark:border-red-900/40 dark:bg-red-950/20"
                }`}
              >
                {p.mapped ? (
                  <div className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-semibold">{p.mapped.nom}</span>
                    <span className="text-muted-foreground">{p.mapped.telephone}</span>
                    {p.mapped.email && (
                      <span className="text-muted-foreground">· {p.mapped.email}</span>
                    )}
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {p.mapped.type_appart.toUpperCase()} · {p.mapped.localisation} · {p.mapped.horizon}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                    <AlertCircle className="h-3 w-3" />
                    Ligne ignorée (nom ou téléphone manquant)
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Separator />

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={skipDup}
          onCheckedChange={(v) => setSkipDup(!!v)}
          data-testid="google-skip-dup"
        />
        <span>Ignorer les doublons (même téléphone)</span>
      </label>

      <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={autoSync}
            onCheckedChange={(v) => setAutoSync(!!v)}
            data-testid="google-autosync"
          />
          <Clock className="h-4 w-4" />
          <span>Activer l'auto-sync de cette feuille</span>
        </label>
        {autoSync && (
          <div className="flex items-center gap-2 pl-7">
            <span className="text-xs text-muted-foreground">Toutes les</span>
            <Input
              type="number"
              min={5}
              className="h-8 w-20"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              data-testid="google-autosync-interval"
            />
            <span className="text-xs text-muted-foreground">minutes (min 5)</span>
          </div>
        )}
      </div>

      <Button
        onClick={handleImport}
        disabled={importing || !tabTitle}
        data-testid="google-import-btn"
        className="w-full gap-2"
      >
        <Download className="h-4 w-4" />
        {importing ? "Import en cours..." : "Importer maintenant"}
      </Button>
    </div>
  );
}

function SyncsPanel({ onChanged }) {
  const [syncs, setSyncs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await googleApi.listSyncs();
      setSyncs(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async (s) => {
    try {
      const res = await googleApi.runSync(s.id);
      if (res.ok) {
        toast.success(
          `Sync "${s.spreadsheet_name}": ${res.inserted} ajoutés, ${res.skipped} ignorés`
        );
      } else {
        toast.error(`Sync échouée: ${res.error || "erreur inconnue"}`);
      }
      load();
      onChanged?.();
    } catch (e) {
      toast.error("Erreur lors du sync");
    }
  };

  const toggle = async (s) => {
    await googleApi.updateSync(s.id, { enabled: !s.enabled });
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer l'auto-sync "${s.spreadsheet_name}" ?`)) return;
    await googleApi.deleteSync(s.id);
    toast.success("Auto-sync supprimée");
    load();
  };

  if (loading && syncs.length === 0) {
    return <div className="p-6 text-center text-sm text-muted-foreground">Chargement...</div>;
  }

  if (syncs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
        Aucune auto-sync configurée. Activez-la depuis l'onglet <strong>Importer</strong>.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="syncs-panel">
      {syncs.map((s) => (
        <div
          key={s.id}
          className="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-3"
        >
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-950/40">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading truncate text-sm font-semibold">
              {s.spreadsheet_name || s.spreadsheet_id}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {s.range} · toutes les {s.interval_minutes} min
              {s.last_run_at && (
                <> · dernière : {formatDateTime(s.last_run_at)}</>
              )}
            </div>
            {s.last_result && (
              <div
                className={`mt-0.5 text-[11px] ${
                  s.last_result.ok
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {s.last_result.ok
                  ? `✓ ${s.last_result.inserted} ajoutés, ${s.last_result.skipped} ignorés`
                  : `✗ ${s.last_result.error}`}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toggle(s)}
              title={s.enabled ? "Désactiver" : "Activer"}
              className="h-8 w-8 p-0"
            >
              {s.enabled ? (
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-zinc-400" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => runNow(s)}
              data-testid={`sync-run-${s.id}`}
              title="Lancer maintenant"
              className="h-8 w-8 p-0"
            >
              <Play className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => remove(s)}
              data-testid={`sync-delete-${s.id}`}
              title="Supprimer"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GoogleSheetsDialog({ open, onOpenChange, onImported }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [tab, setTab] = useState("connect");

  const loadStatus = useCallback(async () => {
    try {
      const s = await googleApi.status();
      setStatus(s);
      // Smart default tab: settings if not configured, import if connected
      if (!s.configured) setTab("settings");
      else if (s.connected && tab === "connect") setTab("import");
    } catch (e) {
      /* ignore */
    }
  }, [tab]);

  useEffect(() => {
    if (open) loadStatus();
  }, [open, loadStatus]);

  // Handle callback redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gflag = params.get("google");
    if (gflag) {
      if (gflag === "connected") {
        toast.success("Google connecté avec succès");
        loadStatus();
      } else if (gflag === "error") {
        toast.error(`Connexion Google échouée : ${params.get("reason") || "inconnue"}`);
      }
      params.delete("google");
      params.delete("reason");
      const rest = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (rest ? `?${rest}` : "")
      );
    }
  }, [loadStatus]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { auth_url } = await googleApi.authStart();
      window.location.href = auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "OAuth non configuré");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Déconnecter Google Sheets ?")) return;
    setBusy(true);
    try {
      await googleApi.disconnect();
      setStatus((s) => ({ ...(s || {}), connected: false, email: null, name: null }));
      setSelectedSheet(null);
      setTab("connect");
      toast.success("Google déconnecté");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto sm:max-w-[840px]"
        data-testid="google-sheets-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Google Sheets · Import & Sync
          </DialogTitle>
          <DialogDescription>
            Connectez votre compte Google, choisissez une feuille, et importez
            automatiquement les contacts comme leads avec programme 20 jours.
          </DialogDescription>
        </DialogHeader>

        <ConnectPanel
          status={status}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          busy={busy}
          onGoToSettings={() => setTab("settings")}
        />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger
              value="import"
              data-testid="tab-google-import"
              disabled={!status?.connected}
            >
              Importer
            </TabsTrigger>
            <TabsTrigger
              value="syncs"
              data-testid="tab-google-syncs"
              disabled={!status?.connected}
            >
              Auto-syncs
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-google-settings">
              <Settings className="mr-1 h-3.5 w-3.5" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import" className="pt-4">
            {status?.connected ? (
              <div className="grid gap-5 md:grid-cols-2">
                <SheetPicker onSelect={setSelectedSheet} selected={selectedSheet} />
                <ImportPanel
                  sheet={selectedSheet}
                  onImported={() => {
                    onImported?.();
                  }}
                />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                Connectez-vous à Google pour voir vos feuilles.
              </div>
            )}
          </TabsContent>

          <TabsContent value="syncs" className="pt-4">
            {status?.connected ? (
              <SyncsPanel onChanged={onImported} />
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                Connectez-vous à Google pour gérer les auto-syncs.
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="pt-4">
            <GoogleConfigPanel onChanged={loadStatus} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
